import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Button,
  Divider,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Skeleton,
  Container,
} from "@mui/material";
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  ComposedChart,
} from "recharts";
import {
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Assignment as OrderIcon,
  Business as FacilityIcon,
  Refresh as RefreshIcon,
  FileDownload as ExportIcon,
  Speed as SpeedIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { orderService } from "../../services/orderService";
import { facilityService } from "../../services/facilityService";
import analyticsService from "../../services/analyticsService";

const Analytics = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [authError, setAuthError] = useState(false);
  const [timeRange, setTimeRange] = useState("6months");
  const [selectedFacility, setSelectedFacility] = useState("all");
  const [availableFacilities, setAvailableFacilities] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Data states
  const [ordersData, setOrdersData] = useState([]);
  const [patientsData, setPatientsData] = useState([]);
  const [facilitiesData, setFacilitiesData] = useState([]);
  const [facilityPatientData, setFacilityPatientData] = useState([]);
  const [summaryStats, setSummaryStats] = useState({
    totalOrders: 0,
    totalPatients: 0,
    totalFacilities: 0,
    ordersGrowth: 0,
    patientsGrowth: 0,
    completionRate: 0,
    avgOrdersPerDay: 0,
  });

  useEffect(() => {
    initializeAnalytics();
  }, []);

  useEffect(() => {
    if (currentUser) {
      fetchAnalyticsData();
    }
  }, [timeRange, selectedFacility, currentUser]);

  const initializeAnalytics = async () => {
    try {
      setAuthError(false);
      setError(null);


      // Get current user info to determine permissions
      const userData = await analyticsService.getCurrentUser();
      setCurrentUser(userData);

      // If super admin, load facility list for filtering
      if (userData.role === "super_admin") {
        try {
          const facilities = await analyticsService.getFacilitiesList();
          setAvailableFacilities(facilities);
        } catch (facilityError) {
          console.warn("Could not load facilities list:", facilityError);
          setAvailableFacilities([]);
        }
      } else {
        setAvailableFacilities([]);
      }
    } catch (error) {
      console.error("Error initializing analytics:", error);

      if (error.message.includes("Authentication failed")) {
        setAuthError(true);
        setError(
          "Your session has expired. Please log in again to access analytics."
        );
      } else {
        setError(
          "Failed to initialize analytics. Please check your connection and try again."
        );
      }
    }
  };

  const fetchAnalyticsData = async () => {
    setLoading(true);
    setError(null);
    setAuthError(false);

    try {

      // Build the facility parameter correctly
      let facilityParam = null;
      if (currentUser?.role === "super_admin" && selectedFacility !== "all") {
        facilityParam = selectedFacility;
      }

      const analyticsData = await analyticsService.getAnalyticsSummary(
        timeRange,
        facilityParam
      );

      if (analyticsData && analyticsData.summary) {

        // Set summary stats with proper number formatting
        setSummaryStats({
          totalOrders: analyticsData.summary.total_orders || 0,
          totalPatients: analyticsData.summary.total_patients || 0,
          totalFacilities: analyticsData.summary.total_facilities || 0,
          ordersGrowth: parseFloat((analyticsData.summary.orders_growth || 0).toFixed(1)),
          patientsGrowth: parseFloat((analyticsData.summary.patients_growth || 0).toFixed(1)),
          completionRate: parseFloat((analyticsData.summary.completion_rate || 0).toFixed(1)),
          avgOrdersPerDay: parseFloat(
            (analyticsData.summary.avg_orders_per_day || 0).toFixed(2)
          ),
        });

        // Process orders growth data
        if (
          analyticsData.orders_growth &&
          Array.isArray(analyticsData.orders_growth)
        ) {
          const formattedOrdersData = analyticsData.orders_growth.map(
            (item) => ({
              ...item,
              displayDate: formatDateForChart(item.date),
            })
          );
          setOrdersData(formattedOrdersData);
        } else {
          console.warn("No orders growth data received, using empty array");
          setOrdersData([]);
        }

        // Process patient growth data
        if (
          analyticsData.patient_growth &&
          Array.isArray(analyticsData.patient_growth)
        ) {
          const formattedPatientsData = analyticsData.patient_growth.map(
            (item) => ({
              ...item,
              displayDate: formatDateForChart(item.date),
            })
          );
          setPatientsData(formattedPatientsData);
        } else {
          console.warn("No patient growth data received, using empty array");
          setPatientsData([]);
        }

        // Process facility performance data
        if (
          analyticsData.facility_performance &&
          Array.isArray(analyticsData.facility_performance)
        ) {
          const formattedFacilityData = analyticsData.facility_performance.map(
            (facility) => ({
              facility:
                facility.facility_name || `Facility ${facility.facility_id}`,
              patients: facility.unique_patients || 0,
              orders: facility.total_orders || 0,
            })
          );
          setFacilityPatientData(formattedFacilityData);
        } else {
          console.warn("No facility performance data received");
          setFacilityPatientData([]);
        }

        setLastUpdated(new Date());
      } else {
        throw new Error("Invalid analytics data structure received");
      }
    } catch (err) {
      console.error("Error fetching analytics data:", err);

      if (err.message.includes("Authentication failed")) {
        setAuthError(true);
        setError(
          "Your session has expired. Please log in again to access analytics."
        );
      } else {
        setError(
          `Failed to load analytics data: ${err.message}. The system will try to process available data.`
        );

        try {
          setSummaryStats({
            totalOrders: 0,
            totalPatients: 0,
            totalFacilities: 0,
            ordersGrowth: 0,
            patientsGrowth: 0,
            completionRate: 0,
            avgOrdersPerDay: 0,
          });
          setOrdersData([]);
          setPatientsData([]);
          setFacilityPatientData([]);
        } catch (fallbackError) {
          console.error("Even fallback failed:", fallbackError);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRetryLogin = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("access_token");
    sessionStorage.removeItem("token");
    sessionStorage.removeItem("access_token");
    navigate("/login");
  };

  const handleRetry = () => {
    if (currentUser) {
      fetchAnalyticsData();
    } else {
      initializeAnalytics();
    }
  };

  const handleDismissError = () => {
    setError(null);
  };

  // Helper functions
  const formatDateForChart = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
    } catch (error) {
      console.warn("Date formatting error:", error);
      return dateString;
    }
  };

  const getGrowthColor = (growth) => {
    if (growth > 0) return "success";
    if (growth < 0) return "error";
    return "default";
  };

  const getGrowthIcon = (growth) => {
    return growth >= 0 ? "↗" : "↘";
  };

  const handleExportData = async () => {
    try {
      setError(null);
      await analyticsService.exportAnalyticsData(
        timeRange,
        selectedFacility,
        "csv"
      );
    } catch (error) {
      console.error("Export failed:", error);
      if (error.message.includes("Authentication failed")) {
        setAuthError(true);
        setError("Authentication failed. Please log in again to export data.");
      } else {
        setError(`Export failed: ${error.message}`);
      }
    }
  };

  // Chart colors
  const COLORS = [
    "#8884d8",
    "#82ca9d",
    "#ffc658",
    "#ff7300",
    "#00c49f",
    "#ff8042",
  ];

  // Custom tooltip for charts
  const CustomTooltip = ({ active, payload, label, formatter }) => {
    if (active && payload && payload.length) {
      return (
        <Paper sx={{ p: 2, border: 1, borderColor: "divider" }}>
          <Typography variant="subtitle2">{label}</Typography>
          {payload.map((entry, index) => (
            <Typography key={index} variant="body2" color={entry.color}>
              {entry.dataKey}:{" "}
              {formatter ? formatter(entry.value) : entry.value}
            </Typography>
          ))}
        </Paper>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >
          <Typography variant="h4" fontWeight="bold">
            Business Analytics
          </Typography>
          <CircularProgress size={24} />
        </Box>
        <Grid container spacing={3}>
          {[1, 2, 3, 4].map((item) => (
            <Grid key={item} size={{ xs: 12, sm: 6, md: 3 }}>
              <Card elevation={3}>
                <CardContent>
                  <Skeleton variant="text" width="60%" />
                  <Skeleton variant="text" width="40%" height={40} />
                  <Skeleton variant="rectangular" width="80%" height={20} />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
        <Box mt={4}>
          <Skeleton variant="rectangular" height={400} />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={4}
      >
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Business Analytics
          </Typography>
          {currentUser?.role === "facility_admin" && (
            <Typography variant="subtitle1" color="textSecondary">
              {currentUser.facility_id
                ? `Facility ${currentUser.facility_id}`
                : "Your Facility"}
            </Typography>
          )}
          {lastUpdated && (
            <Typography variant="caption" color="textSecondary">
              Last updated: {lastUpdated.toLocaleTimeString()}
            </Typography>
          )}
        </Box>
        <Box display="flex" gap={2}>
          {/* Time Range Selector */}
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Time Range</InputLabel>
            <Select
              value={timeRange}
              label="Time Range"
              onChange={(e) => setTimeRange(e.target.value)}
              disabled={authError}
            >
              <MenuItem value="1month">1 Month</MenuItem>
              <MenuItem value="3months">3 Months</MenuItem>
              <MenuItem value="6months">6 Months</MenuItem>
              <MenuItem value="1year">1 Year</MenuItem>
            </Select>
          </FormControl>

          {/* Facility Selector - Only for Super Admin */}
          {currentUser?.role === "super_admin" && (
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Facility</InputLabel>
              <Select
                value={selectedFacility}
                label="Facility"
                onChange={(e) => setSelectedFacility(e.target.value)}
                disabled={authError}
              >
                <MenuItem value="all">All Facilities</MenuItem>
                {availableFacilities.map((facility) => (
                  <MenuItem key={facility.id} value={facility.id}>
                    {facility.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <Button
            startIcon={<ExportIcon />}
            onClick={handleExportData}
            variant="outlined"
            color="secondary"
            disabled={authError}
          >
            Export
          </Button>

          <Button
            startIcon={<RefreshIcon />}
            onClick={handleRetry}
            variant="outlined"
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Error Alert */}
      {error && !authError && (
        <Alert
          severity="warning"
          sx={{ mb: 3 }}
          action={
            <Box>
              <Button color="inherit" size="small" onClick={handleRetry}>
                Retry
              </Button>
              <IconButton
                size="small"
                color="inherit"
                onClick={handleDismissError}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          }
        >
          {error}
        </Alert>
      )}

      {/* FIXED: Key Metrics Cards - Better Sizing */}
      <Grid container spacing={3} sx={{ mb: 6 }}>
        <Grid  size={{ xs: 12, sm: 6, md: 3 }}>
          <Card elevation={3} sx={{ height: 180 }}>
            <CardContent
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <Box>
                  <Typography
                    color="textSecondary"
                    variant="subtitle2"
                    sx={{ fontSize: 14, mb: 1 }}
                  >
                    Total Orders
                  </Typography>
                  <Typography variant="h3" fontWeight="bold" sx={{ mb: 1 }}>
                    {summaryStats.totalOrders.toLocaleString()}
                  </Typography>
                  <Chip
                    label={`${getGrowthIcon(
                      summaryStats.ordersGrowth
                    )} ${Math.abs(summaryStats.ordersGrowth)}%`}
                    color={getGrowthColor(summaryStats.ordersGrowth)}
                    size="small"
                  />
                </Box>
                <OrderIcon color="primary" sx={{ fontSize: 60 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid  size={{ xs: 12, sm: 6, md: 3 }}>
          <Card elevation={3} sx={{ height: 180 }}>
            <CardContent
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <Box>
                  <Typography
                    color="textSecondary"
                    variant="subtitle2"
                    sx={{ fontSize: 14, mb: 1 }}
                  >
                    Total Patients
                  </Typography>
                  <Typography variant="h3" fontWeight="bold" sx={{ mb: 1 }}>
                    {summaryStats.totalPatients.toLocaleString()}
                  </Typography>
                  <Chip
                    label={`${getGrowthIcon(
                      summaryStats.patientsGrowth
                    )} ${Math.abs(summaryStats.patientsGrowth)}%`}
                    color={getGrowthColor(summaryStats.patientsGrowth)}
                    size="small"
                  />
                </Box>
                <PeopleIcon color="secondary" sx={{ fontSize: 60 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid  size={{ xs: 12, sm: 6, md: 3 }}>
          <Card elevation={3} sx={{ height: 180 }}>
            <CardContent
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <Box>
                  <Typography
                    color="textSecondary"
                    variant="subtitle2"
                    sx={{ fontSize: 14, mb: 1 }}
                  >
                    Completion Rate
                  </Typography>
                  <Typography
                    variant="h3"
                    fontWeight="bold"
                    color="success.main"
                    sx={{ mb: 1 }}
                  >
                    {summaryStats.completionRate}%
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Overall efficiency
                  </Typography>
                </Box>
                <CheckCircleIcon color="success" sx={{ fontSize: 60 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid  size={{ xs: 12, sm: 6, md: 3 }}>
          <Card elevation={3} sx={{ height: 180 }}>
            <CardContent
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <Box>
                  <Typography
                    color="textSecondary"
                    variant="subtitle2"
                    sx={{ fontSize: 14, mb: 1 }}
                  >
                    Daily Average
                  </Typography>
                  <Typography
                    variant="h3"
                    fontWeight="bold"
                    color="info.main"
                    sx={{ mb: 1 }}
                  >
                    {summaryStats.avgOrdersPerDay}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Orders per day
                  </Typography>
                </Box>
                <SpeedIcon color="info" sx={{ fontSize: 60 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* FIXED: Charts Section - Much Larger Charts */}
      {!authError && (
        <>
          {/* Orders Growth Chart - FULL WIDTH, MUCH LARGER */}
          <Card elevation={3} sx={{ mb: 6 }}>
            <CardHeader
              title={
                <Typography variant="h5" fontWeight="bold">
                  Orders Growth Over Time{" "}
                  {selectedFacility !== "all" &&
                  currentUser?.role === "super_admin"
                    ? `(${
                        availableFacilities.find(
                          (f) => f.id == selectedFacility
                        )?.name || "Selected Facility"
                      })`
                    : ""}
                </Typography>
              }
              action={
                <Tooltip title="Shows daily order counts and cumulative totals">
                  <InfoIcon color="action" />
                </Tooltip>
              }
              sx={{ pb: 2 }}
            />
            <Divider />
            <CardContent sx={{ p: 4 }}>
              {ordersData.length > 0 ? (
                <ResponsiveContainer width="100%" height={600}>
                  <ComposedChart
                    data={ordersData}
                    margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="displayDate" fontSize={14} />
                    <YAxis yAxisId="left" fontSize={14} />
                    <YAxis yAxisId="right" orientation="right" fontSize={14} />
                    <RechartsTooltip content={<CustomTooltip />} />
                    <Legend />
                    <Area
                      yAxisId="left"
                      type="monotone"
                      dataKey="orders"
                      fill="#8884d8"
                      fillOpacity={0.6}
                      stroke="#8884d8"
                      name="Daily Orders"
                    />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="cumulative"
                      stroke="#82ca9d"
                      strokeWidth={3}
                      name="Cumulative Orders"
                      dot={false}
                    />
                    <Bar
                      yAxisId="left"
                      dataKey="completed"
                      fill="#00c49f"
                      name="Completed"
                      opacity={0.8}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              ) : (
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  height={600}
                >
                  <Typography color="textSecondary" variant="h6">
                    No orders data available for the selected time range
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Patient Registration Growth - FULL WIDTH, LARGE */}
          <Card elevation={3} sx={{ mb: 6 }}>
            <CardHeader
              title={
                <Typography variant="h5" fontWeight="bold">
                  Patient Registration Growth
                </Typography>
              }
              action={
                <Tooltip title="Shows new patient registrations over time">
                  <InfoIcon color="action" />
                </Tooltip>
              }
              sx={{ pb: 2 }}
            />
            <Divider />
            <CardContent sx={{ p: 4 }}>
              {patientsData.length > 0 ? (
                <ResponsiveContainer width="100%" height={600}>
                  <AreaChart
                    data={patientsData}
                    margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="displayDate" fontSize={14} />
                    <YAxis fontSize={14} />
                    <RechartsTooltip content={<CustomTooltip />} />
                    <Legend />
                    <Area
                      type="monotone"
                      dataKey="new_patients"
                      stackId="1"
                      stroke="#ff7300"
                      fill="#ff7300"
                      fillOpacity={0.6}
                      name="New Patients"
                    />
                    <Area
                      type="monotone"
                      dataKey="total_patients"
                      stackId="2"
                      stroke="#00c49f"
                      fill="#00c49f"
                      fillOpacity={0.3}
                      name="Total Patients"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  height={600}
                >
                  <Typography color="textSecondary" variant="h6">
                    No patient data available
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Order Status Distribution - FULL WIDTH, LARGE */}
          <Card elevation={3} sx={{ mb: 6 }}>
            <CardHeader
              title={
                <Typography variant="h5" fontWeight="bold">
                  Order Status Distribution
                </Typography>
              }
              action={
                <Tooltip title="Breakdown of orders by current status">
                  <InfoIcon color="action" />
                </Tooltip>
              }
              sx={{ pb: 2 }}
            />
            <Divider />
            <CardContent sx={{ p: 4 }}>
              {ordersData.length > 0 ? (
                <ResponsiveContainer width="100%" height={600}>
                  <PieChart>
                    <Pie
                      data={[
                        {
                          name: "Completed",
                          value: ordersData.reduce(
                            (sum, day) => sum + day.completed,
                            0
                          ),
                        },
                        {
                          name: "In Progress",
                          value: Math.floor(summaryStats.totalOrders * 0.3),
                        },
                        {
                          name: "Created",
                          value: Math.floor(summaryStats.totalOrders * 0.4),
                        },
                        {
                          name: "Cancelled",
                          value: ordersData.reduce(
                            (sum, day) => sum + day.cancelled,
                            0
                          ),
                        },
                      ].filter((item) => item.value > 0)}
                      cx="50%"
                      cy="50%"
                      outerRadius={220}
                      fill="#8884d8"
                      dataKey="value"
                      label={({ name, percent }) =>
                        `${name} ${(percent * 100).toFixed(0)}%`
                      }
                      fontSize={16}
                    >
                      {COLORS.map((color, index) => (
                        <Cell key={`cell-${index}`} fill={color} />
                      ))}
                    </Pie>
                    <RechartsTooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  height={600}
                >
                  <Typography color="textSecondary" variant="h6">
                    No status data available
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Facility Performance Chart - FULL WIDTH, LARGE */}
          {facilityPatientData.length > 0 && (
            <Card elevation={3} sx={{ mb: 6 }}>
              <CardHeader
                title={
                  <Typography variant="h5" fontWeight="bold">
                    Patients by Facility
                  </Typography>
                }
                action={
                  <Tooltip title="Comparison of patient volumes across facilities">
                    <InfoIcon color="action" />
                  </Tooltip>
                }
                sx={{ pb: 2 }}
              />
              <Divider />
              <CardContent sx={{ p: 4 }}>
                <ResponsiveContainer width="100%" height={500}>
                  <BarChart
                    data={facilityPatientData}
                    margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="facility"
                      angle={-45}
                      textAnchor="end"
                      height={100}
                      interval={0}
                      fontSize={14}
                    />
                    <YAxis
                      label={{
                        value: "Number of Patients",
                        angle: -90,
                        position: "insideLeft",
                      }}
                      fontSize={14}
                    />
                    <RechartsTooltip content={<CustomTooltip />} />
                    <Legend />
                    <Bar dataKey="patients" fill="#8884d8" name="Patients" />
                    <Bar dataKey="orders" fill="#82ca9d" name="Orders" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {/* Performance Insights - LARGER CARDS */}
          <Card elevation={3}>
            <CardHeader
              title={
                <Typography variant="h5" fontWeight="bold">
                  Performance Insights
                </Typography>
              }
              sx={{ pb: 2 }}
            />
            <Divider />
            <CardContent sx={{ p: 4 }}>
              <Grid container spacing={4}>
                <Grid  size={{ xs: 12, md: 3 }}>
                  <Paper
                    elevation={2}
                    sx={{
                      p: 4,
                      height: 160,
                      backgroundColor: "primary.light",
                      color: "primary.contrastText",
                      textAlign: "center",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                    }}
                  >
                    <Typography variant="h6" gutterBottom fontWeight="bold">
                      Best Performing Day
                    </Typography>
                    <Typography variant="h4" sx={{ mb: 1 }}>
                      {ordersData.length > 0
                        ? ordersData.reduce((prev, current) =>
                            prev.orders > current.orders ? prev : current
                          ).displayDate
                        : "N/A"}
                    </Typography>
                  </Paper>
                </Grid>

                <Grid  size={{ xs: 12, md: 3 }}>
                  <Paper
                    elevation={2}
                    sx={{
                      p: 4,
                      height: 160,
                      backgroundColor: "success.light",
                      color: "success.contrastText",
                      textAlign: "center",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                    }}
                  >
                    <Typography variant="h6" gutterBottom fontWeight="bold">
                      Growth Trend
                    </Typography>
                    <Typography variant="h4" sx={{ mb: 1 }}>
                      {summaryStats.ordersGrowth > 0
                        ? "Positive"
                        : summaryStats.ordersGrowth < 0
                        ? "Declining"
                        : "Stable"}
                    </Typography>
                    <Typography variant="body2">
                      {summaryStats.ordersGrowth}% change
                    </Typography>
                  </Paper>
                </Grid>

                <Grid  size={{ xs: 12, md: 3 }}>
                  <Paper
                    elevation={2}
                    sx={{
                      p: 4,
                      height: 160,
                      backgroundColor: "warning.light",
                      color: "warning.contrastText",
                      textAlign: "center",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                    }}
                  >
                    <Typography variant="h6" gutterBottom fontWeight="bold">
                      Efficiency Score
                    </Typography>
                    <Typography variant="h4" sx={{ mb: 1 }}>
                      {summaryStats.completionRate >= 90
                        ? "Excellent"
                        : summaryStats.completionRate >= 75
                        ? "Good"
                        : summaryStats.completionRate >= 60
                        ? "Fair"
                        : "Needs Improvement"}
                    </Typography>
                    <Typography variant="body2">
                      {summaryStats.completionRate}% completion rate
                    </Typography>
                  </Paper>
                </Grid>

                <Grid  size={{ xs: 12, md: 3 }}>
                  <Paper
                    elevation={2}
                    sx={{
                      p: 4,
                      height: 160,
                      backgroundColor: "info.light",
                      color: "info.contrastText",
                      textAlign: "center",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                    }}
                  >
                    <Typography variant="h6" gutterBottom fontWeight="bold">
                      Total Facilities
                    </Typography>
                    <Typography variant="h4" sx={{ mb: 1 }}>
                      {summaryStats.totalFacilities}
                    </Typography>
                    <Typography variant="body2">Active facilities</Typography>
                  </Paper>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </>
      )}

      {/* Authentication Error Dialog */}
      <Dialog open={authError} onClose={() => setAuthError(false)}>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <ErrorIcon color="error" />
            Authentication Required
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Your session has expired or you're not properly authenticated.
            Please log in again to access analytics data.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAuthError(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleRetryLogin}>
            Go to Login
          </Button>
        </DialogActions>
      </Dialog>

      {/* Fallback message when authentication fails */}
      {authError && (
        <Card elevation={3} sx={{ mt: 3 }}>
          <CardContent sx={{ textAlign: "center", py: 8 }}>
            <ErrorIcon sx={{ fontSize: 64, color: "error.main", mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              Authentication Required
            </Typography>
            <Typography variant="body1" color="textSecondary" paragraph>
              Please log in to access your analytics dashboard.
            </Typography>
            <Box sx={{ mt: 3 }}>
              <Button
                variant="contained"
                onClick={handleRetryLogin}
                startIcon={<ErrorIcon />}
                sx={{ mr: 2 }}
              >
                Go to Login
              </Button>
              <Button
                variant="outlined"
                onClick={handleRetry}
                startIcon={<RefreshIcon />}
              >
                Retry
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}
    </Container>
  );
};

export default Analytics;
